import type { AgentPresentation } from "@/lib/agent-presentation-types";
import { clearPendingFramePresentations } from "@/lib/pending-frame-presentation";
import { clearAgentFocusFrames } from "@/lib/agent-focus-presentation";
import { focusOwnerKey } from "@/lib/agent-focus-origin";
import { useCanvasStore } from "@/stores/canvas-store";
import { useProjectStore } from "@/stores/project-store";
import { completePresentationCallouts } from "@/lib/presentation-answer-ownership";

type CleanupScope = "presentation" | "execution";

function presentationSpaceId(presentation: AgentPresentation) {
  if (presentation.projectId) return undefined;
  return presentation.spaceId ?? useProjectStore.getState().currentSpace?.id;
}

function hasPresentationScope(presentation: AgentPresentation): boolean {
  return Boolean(presentation.projectId || presentationSpaceId(presentation));
}

export function clearPresentationVisuals(
  presentation: AgentPresentation,
  scope: CleanupScope = "presentation",
  completed = false,
): void {
  const spaceId = presentationSpaceId(presentation);
  if (!hasPresentationScope(presentation)) return;
  useCanvasStore
    .getState()
    .clearPresentedCursors(
      spaceId,
      scope === "presentation" ? presentation.presentationId : undefined,
      scope === "execution" ? presentation.executionId : undefined,
      completed,
    );
}

export function finishPresentationVisuals(
  presentation: AgentPresentation,
): void {
  const spaceId = presentationSpaceId(presentation);
  if (!hasPresentationScope(presentation)) return;
  completePresentationCallouts(presentation);
  useCanvasStore
    .getState()
    .finishPresentedCursor(spaceId, presentation.presentationId);
}

/** The view moved: the presentation's painted pointer and marks follow it. */
export function followPresentationVisuals(
  presentation: AgentPresentation,
): void {
  const { projectId, presentationId } = presentation;
  if (!projectId) return;
  useCanvasStore.setState((state) => {
    const moved = [...state.agentCursors].filter(
      ([, cursor]) =>
        cursor.presentationDeliveryId === presentationId &&
        cursor.projectId !== projectId,
    );
    if (moved.length === 0) return state;
    const next = new Map(state.agentCursors);
    for (const [agentId, cursor] of moved)
      next.set(agentId, { ...cursor, projectId });
    return { agentCursors: next };
  });
}

export function clearPresentationAnnotations(
  presentation: AgentPresentation,
  scope: CleanupScope = "presentation",
): void {
  const spaceId = presentationSpaceId(presentation);
  if (!hasPresentationScope(presentation)) return;
  useCanvasStore
    .getState()
    .clearPresentedAnnotations(
      spaceId,
      scope === "presentation" ? presentation.presentationId : undefined,
      scope === "execution" ? presentation.executionId : undefined,
    );
}

/** A guide `clear` of frames: its owner's frames leave, all others stay. */
export function clearPresentationFrames(presentation: AgentPresentation): void {
  const spaceId = presentationSpaceId(presentation);
  const ownerId = focusOwnerKey(presentation);
  clearPendingFramePresentations();
  if (ownerId) clearAgentFocusFrames(ownerId);
  if (!hasPresentationScope(presentation)) return;
  useCanvasStore
    .getState()
    .clearPresentedFrameExclusions(spaceId, presentation.presentationId);
}
